import dayjs from 'dayjs';
import { useState } from 'react';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Add as AddIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import { Box, Typography, Card, CardContent, IconButton, TextField, Stack, Tooltip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import { PaymentCreateCustomerProfileDto } from '@libs/dto';
import { usePayment } from '@carcareers/hooks';
import { VisaIcon, MastercardIcon, DiscoverIcon, AmexIcon, JcbIcon, DinersIcon } from '@carcareers/assets';
import { handleChange } from '@carcareers/utils';

interface PaymentCardProps {
  customerPaymentProfileId?: string;
  cardNumber?: string;
  expirationDate?: string;
  cardType?: string;
  onClick?: () => void;
  defaultPaymentProfile?: boolean;
  isNew?: boolean;
  isInput?: boolean;
  selected?: boolean;
  size?: 'small' | 'medium';
}

export const PaymentCard = ({
  customerPaymentProfileId,
  cardNumber,
  expirationDate,
  cardType,
  onClick,
  defaultPaymentProfile = false,
  isNew = false,
  isInput = false,
  selected = false,
  size = 'medium',
}: PaymentCardProps) => {
  const { deletePayment } = usePayment();

  const CardIcon = () => {
    switch (cardType) {
      case 'Visa':
        return <VisaIcon />;
      case 'MasterCard':
        return <MastercardIcon />;
      case 'Discover':
        return <DiscoverIcon />;
      case 'AmericanExpress':
        return <AmexIcon />;
      case 'JCB':
        return <JcbIcon />;
      case 'DinersClub':
        return <DinersIcon />;
      default:
        return <CreditCardIcon />;
    }
  };

  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={(theme) => ({
        border: `2px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        position: 'relative',
        height: size === 'small' ? 150 : 200,
        width: size === 'small' ? 220 : 300,
        transition: 'all 0.1s ease',
        '&:hover':
          isInput || isNew
            ? {
                borderColor: theme.palette.primary.main,
                scale: 1.01,
                cursor: 'pointer',
              }
            : {
                borderColor: theme.palette.divider,
                scale: 1,
                cursor: 'default',
              },
        ...(selected && {
          borderColor: theme.palette.primary.main,
          scale: 1.01,
        }),
      })}
    >
      {isNew ? (
        <Tooltip title="Add New Payment Method" arrow>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 1 }}>
            <AddIcon />
          </Box>
        </Tooltip>
      ) : (
        <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CardIcon />
            {!isInput && (
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                {!isNew && (
                  <Tooltip title="Delete" arrow>
                    <IconButton onClick={() => deletePayment(customerPaymentProfileId)} sx={{ width: 25, height: 25 }}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <>
            <Typography variant="body1" sx={{ mb: 1, textAlign: 'right' }}>
              •••• •••• •••• {cardNumber?.slice(-4)}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {defaultPaymentProfile ? 'Primary' : ''}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, textAlign: 'right' }}>
                Expires: {expirationDate?.slice(5)}/{expirationDate?.slice(2, 4)}
              </Typography>
            </Box>
          </>
        </CardContent>
      )}
    </Card>
  );
};
